import { Box, Button, Menu, MenuItem, Tab, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import axios from 'axios'
import { Config } from 'config'
import { colors, templateImage } from 'constants/theme'
import _ from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import CanvasDraw from 'react-canvas-draw'
import { useTranslation } from 'react-i18next'
import { ToastContainer } from 'react-toastify'
import { getClinicInfo, numberFormat } from 'utils/app.utils'
import {
    getTreatmentPlanPriceDisplay,
    getTreatmentPlanPriceRange,
} from 'component/Treatments/treatment-plan-price-range.utils'
import {
    getTreatmentPlanDocumentConfig,
    getTreatmentPlanDocumentItemRemark,
    getTreatmentPlanDocumentItemRemarkLabelKey,
    getTreatmentPlanDocumentReference,
    getTreatmentPlanNoticeKey,
    getTreatmentPlanPreviewTeeth,
    getTreatmentPlanPreviewLanguage,
    getTreatmentPlanPrintTitle,
    getTreatmentPlanLanguageButtonStyle,
    getTdhToothChartPreviewLayout,
    getTdhTreatmentPlanPrintStyle,
    paginateTdhTreatmentPlanItems,
    setTreatmentPlanPreviewLanguage,
    TreatmentPlanPreviewLanguage,
} from 'component/Treatments/treatment-plan-ui.utils'
import { dateTimeToView, dateToView } from 'utils/date.utils'
import { useSelector } from 'react-redux'
import { treatment, TreatmentStateInterface } from 'app/slice/treatment.slice'
import { getTreatmentPlanDocumentStatus, getTreatmentStatusTranslationKey } from './treatment-plan-status.utils'
import {
    getTreatmentPlanFormActionVisibility,
    getTreatmentPlanValueForDoctor,
    parseTreatmentPlanValueMap,
    TreatmentPlanPrintSignatureState,
} from './treatment-plan-print-history'

// API
import TreatmentsAPI from 'api/dentists/treatment.api'

// Components
import BtnEditUnderline from 'assets/button/BtnEditUnderline'
import { swalCustom } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import { notiError, notiPopup, notiSuccess } from 'component/notifications/notifications'
import FunctionPrint from 'component/Print/print'
import TdhToothChartPreview from 'component/Treatments/TdhToothChartPreview'
import PageModal from 'new-components/modal/page-modal'
import Swal from 'sweetalert2'

/** STYLE */
import * as UseStyled from 'component/Treatments/style/popupTreatmentConsentUseStyled'

// Styled 
import { styled } from '@mui/material/styles'

const StyledModal = styled(Modal)(({ theme }) => ({
    '& .modal-content': {
        padding: '1rem 1.5rem 1.5rem',
        borderRadius: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: 'none !important',
    },
    '& .modal-header': {
        borderBottom: 'none',
        paddingBottom: 0,
    },
    '& .modal-title': {
        width: '100%',
        textAlign: 'center',
        fontSize: 20,
        fontWeight: 600,
        color: colors.themeMainColor,
    },
    '& .modal-footer': {
        borderTop: 'none',
        padding: '1rem 0 0',
    },
    '& .tdh-treatment-plan-dialog': {
        width: 'min(900px, calc(100vw - 2rem))',
        maxWidth: 'min(900px, calc(100vw - 2rem))',
        margin: '1rem auto',
        '& .modal-content': {
            maxHeight: 'calc(100vh - 2rem)',
            padding: '0.75rem 1rem 1rem',
        },
        '& .modal-header': {
            display: 'flex',
            flexShrink: 0,
            position: 'relative',
            justifyContent: 'center',
            padding: '0.25rem 0 0.75rem',
        },
        '& .modal-title': {
            padding: '0 110px',
        },
        '& .tdh-preview-language-trigger': {
            position: 'absolute',
            top: 0,
            right: 0,
            height: 32,
        },
        '& .modal-body': {
            minHeight: 0,
        },
    },
    '& .tdh-doctor-tabs': {
        width: '100%',
        margin: 0,
        borderBottom: '1px solid #e8ecef',
        '& .MuiTabs-root': {
            minHeight: 34,
        },
        '& .MuiTab-root': {
            minHeight: 34,
            minWidth: 0,
            width: 'auto',
            flex: '0 0 auto',
            padding: '6px 18px',
            fontSize: 15,
            textTransform: 'none',
            [theme.breakpoints.up('lg')]: {
                minHeight: 40,
                fontSize: 15,
            },
        },
    },
    '@media (max-width: 768px)': {
        '& .tdh-treatment-plan-dialog': {
            width: '100vw',
            maxWidth: '100vw',
            margin: 0,
            '& .modal-content': {
                minHeight: '100vh',
                maxHeight: '100vh',
                borderRadius: 0,
            },
        },
    },
    '@media (max-width: 600px)': {
        '& .tdh-treatment-plan-dialog .modal-header': {
            flexDirection: 'column',
            alignItems: 'center',
        },
        '& .tdh-treatment-plan-dialog .modal-title': {
            padding: 0,
        },
        '& .tdh-treatment-plan-dialog .tdh-preview-language-trigger': {
            position: 'static',
            alignSelf: 'flex-end',
            marginTop: 8,
        },
    }
}))

const PageContent = styled(Box)(({ theme }) => ({
    backgroundColor: 'transparent',
    padding: '16px',
    color: '#333',
    fontFamily: 'Sarabun, sans-serif'
}))

const TablePlan = styled('table')(({ theme }) => ({
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    marginBottom: '1rem',
    '& tr.data-row td, & tr.header-row th': {
        border: `1px solid #C4C4C4`,
        padding: '12px 8px',
        textAlign: 'center',
        fontSize: '14px',
        color: '#333'
    },
    '& tr.header-row th': {
        fontWeight: 600,
        backgroundColor: '#fff',
    }
}))

const TdhDocumentTableScroll = styled(Box)(({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    '& table': {
        minWidth: 0,
    },
}))

const TdhDocumentToothChart = styled(Box)(({ theme }) => ({
    ...(() => {
        const layout = getTdhToothChartPreviewLayout()
        return {
            height: `${layout.previewHeight}px`,
            minHeight: `${layout.previewHeight}px`,
        }
    })(),
    width: '100%',
    overflow: 'hidden',
    border: '1px solid #999',
    margin: '6px 0 8px',
}))

// Shared by the on-screen document and its cloned print content.
const TdhDocumentPage = styled(PageContent)({
    width: '100%',
    maxWidth: 'none',
    minHeight: '277mm',
    boxSizing: 'border-box',
    margin: 0,
    padding: '6mm 8mm',
    background: '#fff',
    color: '#111',
    fontSize: 11,
    lineHeight: 1.4,
    '& .tdh-document-header': {
        display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr) 180px', gap: 10, alignItems: 'start',
    },
    '& .tdh-document-header > div:first-of-type': { display: 'flex', alignItems: 'center' },
    '& .tdh-document-header img': { width: 100, height: 65, objectFit: 'contain' },
    '& .tdh-clinic-name': { fontSize: 23, fontWeight: 700, textAlign: 'center' },
    '& .tdh-document-meta': { alignSelf: 'center', textAlign: 'right', fontSize: 10 },
    '& .tdh-clinic-contact': { textAlign: 'center', margin: '10px 0 6px' },
    '& .tdh-document-title': { textAlign: 'center', fontSize: 18, fontWeight: 700 },
    '& .tdh-document-subtitle': { textAlign: 'center', fontSize: 12, color: '#555', marginBottom: 6 },
    '& .tdh-plan-table': { margin: 0, width: '100%' },
    '& .tdh-plan-table tr.data-row td, & .tdh-plan-table tr.header-row th': {
        fontSize: 10, padding: '6px 5px', borderColor: '#555',
    },
    '& .tdh-plan-table tr.data-row td .MuiTypography-root': { fontSize: 10, lineHeight: 1.3 },
    '& .tdh-plan-table tr.data-row td .tdh-item-remark': { fontSize: 8.5, color: '#555' },
    '& .tdh-plan-table .total-row td': { border: '1px solid #555', padding: '4px 5px', fontSize: 10 },
    '& .tdh-plan-table tr': { breakInside: 'avoid' },
    '& .tdh-plan-table thead': { display: 'table-header-group' },
    '& .tdh-signatures': { breakInside: 'avoid', marginTop: '18px !important', paddingTop: '0 !important' },
    '& .tdh-signatures .MuiTypography-root': { fontSize: 10, fontWeight: 400 },
    '& .tdh-signatures button': { height: 65, width: '100%', fontSize: 10 },
    '& .tdh-signatures img': { maxHeight: 60 },
    '& .tdh-document-footer': { marginTop: 8, fontSize: 8, color: '#777', textAlign: 'center' },
    '& .tdh-continuation-header': { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #999', paddingBottom: 8, marginBottom: 12, fontSize: 12 },
    '& .tdh-page-number': { marginTop: 10, textAlign: 'right', fontSize: 9, color: '#777' },
    '&.tdh-print-page': {
        position: 'relative',
        minHeight: '280mm',
        paddingBottom: '5mm',
        '& .tdh-page-number': { position: 'absolute', right: 0, bottom: 0, marginTop: 0 },
    },
    '& .tdh-final-section': { breakInside: 'avoid', pageBreakInside: 'avoid' },
    '@media print': {
        width: '100%', minHeight: 0, padding: 0, margin: 0,
        '& .tdh-signatures': {
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            marginTop: '6px !important',
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
        },
        '& .tdh-signatures button': {
            border: 'none',
            height: '48px !important',
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
        },
        '& .tdh-signatures img': { maxHeight: '44px !important' },
        '& .tdh-signatures .MuiTypography-root': { fontSize: '8px !important', lineHeight: '1.1 !important' },
        '& .tdh-document-footer': { marginTop: '2px', fontSize: 7 },
        '& .tdh-signatures button svg, & .tdh-signatures button .MuiTypography-root': { visibility: 'hidden' },
    },
})

const SignBoxFee = styled(Box)(({ theme }) => ({
    width: '100%',
    position: 'relative',
    '> img': {
        position: 'absolute',
        top: 0,
    },
    '.canvas-box > div': {
        margin: 'auto',
        width: '100% !important',
        height: '400px !important',
        border: `1px solid ${colors.lightGray}`,
        borderRadius: '4px',
    },
    '.clear-box': {
        opacity: 1,
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 5,
        '.MuiButton-root': {
            padding: '0 12px',
            minWidth: 'unset',
            borderColor: `${colors.themeThirdColor} !important`,
            color: `${colors.themeThirdColor} !important`,
            backgroundColor: `${colors.white} !important`,
        }
    }
}))

const StyledSignatureButton = styled(Button)`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 240px;
  height: 110px;
  border: 1px dashed #e5e5e5;
  padding: 8px 0px;
  color: #808080;
  background-color: transparent;
  &:hover {
    border-color: #808080;
    background-color: #f5f5f5;
  }
  @media print {
     & {
        border: none !important;
    }
  }
`;

const resolveBranchImage = (image?: string) => {
    const imagePath = `${image || ''}`.trim()
    if (!imagePath) return ''
    if (/^(data:|blob:|https?:\/\/|\/\/|\/)/i.test(imagePath)) return imagePath

    const imageBaseUrl = `${Config.REACT_APP_IMAGE_URL || ''}`.replace(/\/+$/, '')
    return imageBaseUrl ? `${imageBaseUrl}/${imagePath.replace(/^\/+/, '')}` : imagePath
}

const onBranchLogoError = ({ currentTarget }: any) => {
    currentTarget.onerror = null
    currentTarget.src = templateImage.logoHorizontal
}

type Props = {
    isShow: boolean
    setIsShow: (show: boolean) => void
    plan: any
    patient: any
    doctors: any[]
    onSaved?: () => void
    onAfterPrint?: (signatureState: TreatmentPlanPrintSignatureState) => void
    isReprint?: boolean
}

export default function PopupTreatmentPlanForm(props: Props) {
    const { t, i18n } = useTranslation()
    const printRef = useRef<HTMLDivElement>(null)
    const showTdhNotice = Config.IS_TDH === '1'
    const [previewLanguage, setPreviewLanguage] = useState<TreatmentPlanPreviewLanguage>(() => {
        try {
            return getTreatmentPlanPreviewLanguage(window.localStorage)
        } catch (_) {
            return 'th'
        }
    })
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<HTMLElement | null>(null)
    const systemLanguage = `${i18n.language || ''}`.toLowerCase().startsWith('en') ? 'en' : 'th'
    const documentLanguage: TreatmentPlanPreviewLanguage = showTdhNotice ? previewLanguage : systemLanguage
    const documentT = (key: string) => t(key, { lng: documentLanguage })
    const documentConfig = getTreatmentPlanDocumentConfig(showTdhNotice)
    const translatedDocumentTitle = documentT(documentConfig.titleKey)
    const documentTitle = translatedDocumentTitle === documentConfig.titleKey ? documentT('TXP.TITLE') : translatedDocumentTitle
    const isEnglish = documentLanguage === 'en'
    const tdhLabel = (key: string) => documentT(`TXP.PRINT.TDH.${key}`)

    const allItems = props.plan?.patientTreatmentPlanItems || []
    const treatments: TreatmentStateInterface = useSelector(treatment)
    const documentStatus = props.plan?.status
        ? getTreatmentPlanDocumentStatus({
            planStatus: props.plan.status,
            items: allItems,
            activeTreatmentIds: _.map(treatments?.summeryTreatments || [], 'refId'),
        })
        : null
    const uniqueDoctorIds: number[] = _.uniq(allItems.map((item: any) => Number(item.doctorId || 0)))
    const [doctorTab, setDoctorTab] = useState(0)

    // Store signatures per doctor. Key is doctorId.
    const [signatures, setSignatures] = useState<Record<number, { doctor: string, patient: string }>>({})
    const [signedAtByDoctor, setSignedAtByDoctor] = useState<Record<number, { doctor: string, patient: string }>>({})
    const [savedDoctorIds, setSavedDoctorIds] = useState<Set<number>>(new Set())

    const [openSignPad, setOpenSignPad] = useState(false)
    const [modalType, setModalType] = useState<'doctor' | 'patient'>('doctor')

    const canvasDoctorRef: any = useRef()
    const canvasPatientRef: any = useRef()
    const [canvasKey, setCanvasKey] = useState(Date.now())

    const branchInfo = props.plan?.branch || getClinicInfo() || {}
    const branchLogo = resolveBranchImage(branchInfo?.branchLogo || branchInfo?.branchImage) || templateImage.logoHorizontal
    const clinicName = isEnglish
        ? [branchInfo?.clinicNameEn || branchInfo?.clinicAndBranchName || branchInfo?.clinicName, branchInfo?.branchNameEn || branchInfo?.branchName].filter(Boolean).join(' - ')
        : branchInfo?.clinicAndBranchName || `${branchInfo?.clinicName || branchInfo?.clinicNameEn || ''} ${branchInfo?.branchName ? `สาขา ${branchInfo.branchName}` : ''}`.trim()
    const clinicAddress = isEnglish
        ? branchInfo?.branchFullAddressEn || branchInfo?.branchFullAddress || ''
        : branchInfo?.branchFullAddress || branchInfo?.branchFullAddressEn || ''

    const activeDoctorId = uniqueDoctorIds[doctorTab]
    const groupedItems = showTdhNotice ? allItems : allItems.filter((item: any) => (item.doctorId || 0) === activeDoctorId)
    const activeDoctorData = props.doctors?.find(d => d.userId === activeDoctorId) || { fullname: props.plan?.createdBy || '-' }
    const doctorName = isEnglish
        ? activeDoctorData.fullnameEn || [activeDoctorData.firstnameEn, activeDoctorData.lastnameEn].filter(Boolean).join(' ') || activeDoctorData.fullname || '-'
        : activeDoctorData.fullname || [activeDoctorData.firstname, activeDoctorData.lastname].filter(Boolean).join(' ') || '-'
    const toothChartType = groupedItems.find((item: any) => item.teethType === 'CHILD')?.teethType || groupedItems.find((item: any) => item.teethType === 'ADULT')?.teethType || 'ADULT'
    const tdhPreviewTeeth = useMemo(() => getTreatmentPlanPreviewTeeth(groupedItems), [groupedItems])
    const treatmentPlanNoticeKey = getTreatmentPlanNoticeKey(documentLanguage)
    const treatmentPlanRemarkLabel = documentT('TXP.PRINT.TABLE.REMARK')
    const treatmentPlanItemRemarkLabel = documentT(getTreatmentPlanDocumentItemRemarkLabelKey())
    const patientName = isEnglish
        ? `${props.patient?.firstnameEn || props.patient?.firstname || ''} ${props.patient?.lastnameEn || props.patient?.lastname || ''}`.trim()
        : `${props.patient?.firstname || props.patient?.firstnameEn || ''} ${props.patient?.lastname || props.patient?.lastnameEn || ''}`.trim()
    const documentReference = getTreatmentPlanDocumentReference(props.plan)
    const printTitle = getTreatmentPlanPrintTitle(documentTitle, documentReference)
    const issuedDate = props.plan?.createdAt ? dateToView(props.plan.createdAt, true, isEnglish ? 'en' : 'th') : '-'
    const signedAt = (value: any) => {
        if (!value) return ''
        if (!isEnglish) return dateTimeToView(value, true, 'th')
        return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date(value))
    }

    const doctorSignedAt = signedAtByDoctor[activeDoctorId]?.doctor || getTreatmentPlanValueForDoctor(props.plan?.doctorSignedAt, activeDoctorId)
    const patientSignedAt = signedAtByDoctor[activeDoctorId]?.patient || getTreatmentPlanValueForDoctor(props.plan?.patientSignedAt, activeDoctorId)

    const handlePreviewLanguageChange = (language: TreatmentPlanPreviewLanguage) => {
        setLanguageMenuAnchor(null)
        let storage: Storage | undefined
        try {
            storage = window.localStorage
        } catch (_) {
            storage = undefined
        }
        setPreviewLanguage(setTreatmentPlanPreviewLanguage(storage, language))
    }

    const isActiveDoctorSaved = savedDoctorIds.has(activeDoctorId)
    const hasAnySaved = savedDoctorIds.size > 0
    const hasBothSignatures = !!(signatures[activeDoctorId]?.doctor && signatures[activeDoctorId]?.patient)
    const { showSave, showCancelDocument } = getTreatmentPlanFormActionVisibility({
        isReprint: props.isReprint,
        isActiveDoctorSaved,
        hasAnySaved,
    })

    const convertImageToBase64 = async (url: string): Promise<string> => {
        const response = await axios.get(url, { responseType: 'blob' })
        const blob = response.data
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = (error) => reject(error)
            reader.readAsDataURL(blob)
        })
    }

    useEffect(() => {
        if (!props.isShow) return

        // Load saved signatures and their timestamps from plan data (stored as JSON keyed by doctorId).
        const docSigs = parseTreatmentPlanValueMap(props.plan?.doctorSignature)
        const patSigs = parseTreatmentPlanValueMap(props.plan?.patientSignature)
        const doctorSignedAtMap = parseTreatmentPlanValueMap(props.plan?.doctorSignedAt)
        const patientSignedAtMap = parseTreatmentPlanValueMap(props.plan?.patientSignedAt)
        const initialSignatures: Record<number, { doctor: string, patient: string }> = {}
        const initialSignedAt: Record<number, { doctor: string, patient: string }> = {}
        const saved = new Set<number>()

        uniqueDoctorIds.forEach((docId) => {
            const key = String(docId)
            const doctorSignature = docSigs[key] || ''
            const patientSignature = patSigs[key] || ''
            if (doctorSignature || patientSignature) {
                initialSignatures[docId] = { doctor: doctorSignature, patient: patientSignature }
            }
            if (doctorSignature && patientSignature) saved.add(docId)

            const doctorSignedAt = doctorSignedAtMap[key]
            const patientSignedAt = patientSignedAtMap[key]
            if (doctorSignedAt || patientSignedAt) {
                initialSignedAt[docId] = { doctor: doctorSignedAt || '', patient: patientSignedAt || '' }
            }
        })

        setSignatures(initialSignatures)
        setSignedAtByDoctor(initialSignedAt)
        setSavedDoctorIds(saved)

        // Load doctor signature from doctor profile for unsigned doctors
        uniqueDoctorIds.forEach((docId) => {
            const docData = props.doctors?.find((d: any) => d.userId === docId)
            if (docData?.doctorSignature) {
                convertImageToBase64(`${Config.REACT_APP_IMAGE_URL}/${docData.doctorSignature}`)
                    .then((base64) => {
                        let base64string: any = `${base64}`.split(',')
                        base64string = base64string[base64string.length - 1]
                        setSignatures(prev => {
                            // Don't overwrite if already loaded from saved plan
                            if (prev[docId]?.doctor) return prev
                            return {
                                ...prev,
                                [docId]: { ...(prev[docId] || { doctor: '', patient: '' }), doctor: base64string }
                            }
                        })
                    })
                    .catch((error) => console.error('Error loading doctor signature:', error))
            }
        })
    }, [props.isShow, props.plan])

    const onSave = async () => {
        const planId = props.plan?.patientTreatmentPlanId
        if (!planId) return
        const currentSig = signatures[activeDoctorId]
        if (!currentSig?.doctor || !currentSig?.patient) return

        await TreatmentsAPI.saveTreatmentPlanSignature(planId, {
            doctorId: activeDoctorId,
            doctorSignature: currentSig.doctor,
            patientSignature: currentSig.patient
        }).then((response) => {
            const savedPlan = response?.data || response
            const fallbackSignedAt = new Date().toISOString()
            const savedDoctorSignedAt = getTreatmentPlanValueForDoctor(savedPlan?.doctorSignedAt, activeDoctorId) || fallbackSignedAt
            const savedPatientSignedAt = getTreatmentPlanValueForDoctor(savedPlan?.patientSignedAt, activeDoctorId) || fallbackSignedAt

            notiPopup('clear')
            setSignedAtByDoctor(prev => ({
                ...prev,
                [activeDoctorId]: { doctor: savedDoctorSignedAt, patient: savedPatientSignedAt },
            }))
            setSavedDoctorIds(prev => new Set(prev).add(activeDoctorId))
            notiSuccess(t('MODAL_TREATMENT_CONSENT.MESSAGE.SUCCESS.SAVE') || 'บันทึกสำเร็จ')
            props.onSaved?.()
        }).catch(() => {
            notiError(t('MODAL_TREATMENT_CONSENT.MESSAGE.ERROR.DEFAULT') || 'เกิดข้อผิดพลาด')
        })
    }

    const handleAfterPrint = () => {
        props.onAfterPrint?.({
            signaturesByDoctor: signatures,
            signedAtByDoctor,
            savedDoctorIds: Array.from(savedDoctorIds),
        })
    }

    const onCancel = () => {
        Swal.fire({
            title: t('MODAL_TREATMENT_CONSENT.BUTTON.CANCEL_DOCUMENT') || 'ยกเลิกเอกสาร',
            html: t('MODAL_TREATMENT_CONSENT.MESSAGE.CONFIRM_CANCEL') || 'ต้องการยกเลิกเอกสารใช่หรือไม่?',
            icon: 'warning',
            width: 450,
            showCloseButton: false,
            showCancelButton: true,
            reverseButtons: true,
            focusConfirm: false,
            confirmButtonText: 'ยืนยัน',
            confirmButtonColor: colors.themeThirdColor,
            cancelButtonText: 'ยกเลิก',
            customClass: { container: 'swal2-warning' }
        }).then(async (result) => {
            if (!result.isConfirmed) return
            const planId = props.plan?.patientTreatmentPlanId
            if (!planId) return

            await TreatmentsAPI.deleteTreatmentPlanSignature(planId).then(() => {
                const clearedSigs: Record<number, { doctor: string, patient: string }> = {}
                uniqueDoctorIds.forEach((docId) => {
                    clearedSigs[docId] = { doctor: '', patient: '' }
                })
                setSignatures(clearedSigs)
                setSignedAtByDoctor({})
                setSavedDoctorIds(new Set())
                notiSuccess(t('MODAL_TREATMENT_CONSENT.MESSAGE.SUCCESS.CANCEL') || 'ยกเลิกเอกสารสำเร็จ')
                props.onSaved?.()
            }).catch(() => {
                notiError(t('MODAL_TREATMENT_CONSENT.MESSAGE.ERROR.DEFAULT') || 'เกิดข้อผิดพลาด')
            })
        })
    }

    const handleClose = () => {
        const hasAnyUnsaved = uniqueDoctorIds.some((docId) => {
            const sig = signatures[docId]
            return !!(sig?.doctor || sig?.patient) && !savedDoctorIds.has(docId)
        })

        if (hasAnyUnsaved) {
            swalCustom(
                t('MODAL_TREATMENT_CONSENT.MESSAGE.INPROGRESS') || 'ยังไม่ได้บันทึกลายเซ็น',
                t('MODAL_TREATMENT_CONSENT.MESSAGE.CONFIRM_CLOSE') || 'ลายเซ็นที่ยังไม่ได้บันทึกจะหายไป ต้องการปิดใช่หรือไม่?',
                (confirmed: boolean) => {
                    if (!confirmed) return
                    notiPopup('clear')
                    props.setIsShow(false)
                },
                'warning',
                { showCloseButton: false }
            )
            return
        }

        notiPopup('clear')
        props.setIsShow(false)
    }

    const handleClearSignature = (type: 'doctor' | 'patient') => {
        if (type === 'doctor' && canvasDoctorRef.current) {
            canvasDoctorRef.current.clear()
            setSignatures(prev => ({ ...prev, [activeDoctorId]: { ...prev[activeDoctorId], doctor: '' } }))
        }
        if (type === 'patient' && canvasPatientRef.current) {
            canvasPatientRef.current.clear()
            setSignatures(prev => ({ ...prev, [activeDoctorId]: { ...prev[activeDoctorId], patient: '' } }))
        }
        setCanvasKey(Date.now())
    }

    const handleSaveSignature = (type: 'doctor' | 'patient') => {
        const ref = type === 'doctor' ? canvasDoctorRef : canvasPatientRef
        if (ref.current) {
            const base64 = ref.current.canvasContainer.childNodes[1].toDataURL('image/png')
            let base64string: any = `${base64}`.split(',')
            base64string = base64string[base64string.length - 1]
            setSignatures(prev => ({ ...prev, [activeDoctorId]: { ...(prev[activeDoctorId] || { doctor: '', patient: '' }), [type]: base64string } }))
        }
        setOpenSignPad(false)
    }

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        const currentSig = signatures[activeDoctorId]
        const hasUnsaved = !!(currentSig?.doctor || currentSig?.patient) && !savedDoctorIds.has(activeDoctorId)

        if (hasUnsaved) {
            notiPopup('clear')
            notiPopup('custom', '', (
                <div className={'d-flex align-items-center justify-content-between'}>
                    <span>{t('MODAL_TREATMENT_CONSENT.MESSAGE.INPROGRESS') || 'ยังไม่ได้บันทึกลายเซ็น'}</span>
                    <Button variant="text" disableRipple onClick={() => {
                        setSignatures(prev => ({ ...prev, [activeDoctorId]: { doctor: '', patient: '' } }))
                        notiPopup('clear')
                        setDoctorTab(newValue)
                        setTimeout(() => setCanvasKey(Date.now()), 100)
                    }}
                        sx={{ color: colors.statusWarning, fontSize: 16, fontWeight: 400, padding: 0, lineHeight: 1, backgroundColor: 'transparent !important' }}>
                        {t('MODAL_TREATMENT_CONSENT.BUTTON.DISCARD') || 'ละทิ้ง'}
                    </Button>
                </div>
            ))
            return
        }

        setDoctorTab(newValue)
        setTimeout(() => setCanvasKey(Date.now()), 100)
    }

    const handleTotal = () => {
        return groupedItems?.reduce((acc: number, item: any) => acc + (item.price || 0), 0) || 0
    }

    const renderPlanPrice = (item: any) => {
        if (!showTdhNotice) return item.price ? numberFormat(item.price) : '-'

        const display = getTreatmentPlanPriceDisplay(item)
        if (display.range) return `${numberFormat(display.range.min)} – ${numberFormat(display.range.max)}`
        return display.primary !== null ? numberFormat(display.primary) : '-'
    }

    const renderPlanTotal = () => {
        if (!showTdhNotice) return numberFormat(handleTotal())

        const ranges: Array<ReturnType<typeof getTreatmentPlanPriceRange>> = groupedItems.map((item: any) => getTreatmentPlanPriceRange(item))
        const hasMeaningfulRange = ranges.some((range) => range && range.min !== range.max)
        if (!hasMeaningfulRange) return numberFormat(handleTotal())

        const minTotal = groupedItems.reduce((total: number, item: any, index: number) => (
            total + (ranges[index]?.min ?? (Number(item.price) || 0))
        ), 0)
        const maxTotal = groupedItems.reduce((total: number, item: any, index: number) => (
            total + (ranges[index]?.max ?? (Number(item.price) || 0))
        ), 0)
        return `${numberFormat(minTotal)} – ${numberFormat(maxTotal)}`
    }

    const renderTdhItemRemark = (item: any) => {
        const remark = getTreatmentPlanDocumentItemRemark(item)
        if (!remark) return null
        return (
            <Typography component="div" className="tdh-item-remark" sx={{ color: '#999', fontSize: 12, lineHeight: 1.35, mt: 0.25 }}>
                {treatmentPlanItemRemarkLabel}: {remark}
            </Typography>
        )
    }

    const renderTdhPageUI = (forPrint: boolean) => {
        const pages = forPrint ? paginateTdhTreatmentPlanItems(groupedItems) : [groupedItems]
        return (
        <Box
            ref={forPrint ? printRef : undefined}
            className={forPrint ? 'print-content tdh-plan-document tdh-print-source' : 'tdh-plan-preview'}
            style={forPrint
                ? { display: 'none' }
                : { width: '210mm', maxWidth: 'none', margin: '0 auto', boxSizing: 'border-box' }}
        >
        {pages.map((pageItems, pageIndex) => {
            const isFirstPage = pageIndex === 0
            const isLastPage = pageIndex === pages.length - 1
            const pageStartIndex = pages.slice(0, pageIndex).reduce((count, page) => count + page.length, 0)
            return (
        <TdhDocumentPage key={pageIndex} className={forPrint ? 'tdh-document-page tdh-print-page' : 'tdh-document-page'}>
            {isFirstPage ? <>
            <div className="tdh-document-header">
                <div><img src={branchLogo} onError={onBranchLogoError} alt={tdhLabel('CLINIC_LOGO')} /></div>
                <div>
                    <div className="tdh-clinic-name">{clinicName || '-'}</div>
                </div>
                <div className="tdh-document-meta">
                    <div>{tdhLabel('DOCUMENT_NUMBER')}: {documentReference || tdhLabel('DOCUMENT_PENDING')}</div>
                    <div>{tdhLabel('ISSUED_DATE')}: {issuedDate}</div>
                    <div>{tdhLabel('STATUS')}: {documentStatus ? documentT(getTreatmentStatusTranslationKey(documentStatus, true)) : '-'}</div>
                </div>
            </div>
            <div className="tdh-clinic-contact">
                <div>{clinicAddress}{branchInfo?.branchPhone1 ? ` ${tdhLabel('PHONE')} ${branchInfo.branchPhone1}` : ''}</div>
            </div>
            <div className="tdh-document-title">{tdhLabel('TITLE')}</div>
            <div className="tdh-document-subtitle">{tdhLabel('SUBTITLE')}</div>
            <div><strong>{tdhLabel('PATIENT_NAME')} : </strong>{patientName || '-'}</div>
            <div><strong>{tdhLabel('PATIENT_ID')} : </strong>{props.patient?.branchCnNumber || props.patient?.cnNumber || props.patient?.hn || '-'}{'　'}
                <strong>{tdhLabel('DATE_OF_BIRTH')} : </strong>{props.patient?.dateOfBirth ? dateToView(props.patient.dateOfBirth, true, isEnglish ? 'en' : 'th') : '-'}{'　'}
                <strong>{tdhLabel('GENDER')} : </strong>{props.patient?.gender ? documentT(`GENDER_OPTION.${props.patient.gender}`) : '-'}</div>
            <div><strong>{tdhLabel('TREATMENT_PLAN')} : </strong>{props.plan?.treatmentPlanName || '-'}</div>
            <TdhDocumentToothChart aria-label={tdhLabel('TOOTH_CHART')}>
                <TdhToothChartPreview teeth={tdhPreviewTeeth} teethType={toothChartType} />
            </TdhDocumentToothChart>
            </> : <div className="tdh-continuation-header">
                <strong>{clinicName || '-'}</strong>
                <span>{tdhLabel('TITLE')} · {documentReference || tdhLabel('DOCUMENT_PENDING')}</span>
            </div>}
            <TdhDocumentTableScroll className="tdh-document-table-scroll">
                <TablePlan className="tdh-plan-table" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '50%' }} />
                        <col style={{ width: '25%' }} />
                    </colgroup>
                    <thead>
                        <tr className="header-row">
                            <th>#</th>
                            <th>{documentT('TXP.PRINT.TABLE.TEETH')}</th>
                            <th>{tdhLabel('OPERATIVE')}</th>
                            <th>{tdhLabel('ESTIMATED_PRICE')}<br />{tdhLabel('CURRENCY')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((item: any, index: number) => (
                            <tr key={item.id || index} className="data-row">
                                <td>{pageStartIndex + index + 1}</td>
                                <td style={{ wordBreak: 'break-word' }}>{item.teeth || '-'}</td>
                                <td style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                                    <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.4 }}>
                                        {(isEnglish ? item.operativeNameEn || item.operativeName : item.operativeName || item.operativeNameEn) || '-'}
                                    </Typography>
                                    {renderTdhItemRemark(item)}
                                </td>
                                <td style={{ textAlign: 'right', overflowWrap: 'anywhere' }}>{renderPlanPrice(item)}</td>
                            </tr>
                        ))}
                        {isLastPage && <tr className="total-row">
                            <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>
                                {tdhLabel('TOTAL')}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{renderPlanTotal()}</td>
                        </tr>}
                    </tbody>
                </TablePlan>
            </TdhDocumentTableScroll>

            {isLastPage && <div className="tdh-final-section">
            <Box sx={{ mt: 1, mb: 1.5, fontSize: 11, color: '#333' }}>
                <strong>{treatmentPlanRemarkLabel}:</strong> {props.plan?.remark || '-'}
                <Box className="tdh-document-notice" sx={{ mt: 1, fontSize: 10, lineHeight: 1.35, color: '#555' }}>
                    <div>* {documentT(treatmentPlanNoticeKey)}</div>
                </Box>
            </Box>

            <Box className="tdh-signatures d-flex justify-content-between mt-4 pt-4 pb-2 px-3">
                <Box sx={{ width: 340, maxWidth: '45%', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <StyledSignatureButton disabled={isActiveDoctorSaved || props.isReprint} onClick={() => { setModalType('doctor'); setOpenSignPad(true); }}>
                            {signatures[activeDoctorId]?.doctor ? (
                                <img src={`data:image/png;base64,${signatures[activeDoctorId]?.doctor}`} style={{ width: '160px', height: '90px', objectFit: 'contain' }} alt="doctor sig" />
                            ) : (
                                <>
                                    <BtnEditUnderline color={'#808080'} width="28" height="28" />
                                    <Typography sx={{ fontWeight: '500', fontSize: '14px' }}>{tdhLabel('CLICK_SIGNATURE')}</Typography>
                                </>
                            )}
                        </StyledSignatureButton>
                    </Box>
                    <Box sx={{ borderBottom: '1px dashed black', width: '200px', maxWidth: '100%', margin: '0 auto', mb: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{`( ${doctorName} )`}</Typography>
                    <Typography sx={{ fontSize: 14, color: '#666' }}>{tdhLabel('DOCTOR_ROLE')}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#999' }}>{tdhLabel('DOCTOR_SIGNATURE')}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#777' }}>{tdhLabel('SIGNED_AT')} {signedAt(doctorSignedAt)}</Typography>
                </Box>

                <Box sx={{ width: 340, maxWidth: '45%', textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <StyledSignatureButton disabled={isActiveDoctorSaved || props.isReprint} onClick={() => { setModalType('patient'); setOpenSignPad(true); }}>
                            {signatures[activeDoctorId]?.patient ? (
                                <img src={`data:image/png;base64,${signatures[activeDoctorId]?.patient}`} style={{ width: '160px', height: '90px', objectFit: 'contain' }} alt="patient sig" />
                            ) : (
                                <>
                                    <BtnEditUnderline color={'#808080'} width="28" height="28" />
                                    <Typography sx={{ fontWeight: '500', fontSize: '14px' }}>{tdhLabel('CLICK_SIGNATURE')}</Typography>
                                </>
                            )}
                        </StyledSignatureButton>
                    </Box>
                    <Box sx={{ borderBottom: '1px dashed black', width: '200px', maxWidth: '100%', margin: '0 auto', mb: 1 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{`( ${patientName} )`}</Typography>
                    <Typography sx={{ fontSize: 14, color: '#666' }}>{tdhLabel('PATIENT_ROLE')}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#999' }}>{tdhLabel('PATIENT_SIGNATURE')}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#777' }}>{tdhLabel('SIGNED_AT')} {signedAt(patientSignedAt)}</Typography>
                </Box>
            </Box>
            <div className="tdh-document-footer">{tdhLabel('FOOTER')}{documentReference ? ` | ${tdhLabel('DOCUMENT')} ${documentReference}` : ''}</div>
            </div>}
            {forPrint && pages.length > 1 && <div className="tdh-page-number">{isEnglish ? 'Page' : 'หน้า'} {pageIndex + 1} / {pages.length}</div>}
        </TdhDocumentPage>
            )
        })}
        </Box>
        )
    }
    const TdhPageUI = <>{renderTdhPageUI(false)}{renderTdhPageUI(true)}</>

    const LegacyPageUI = (
        <PageContent ref={printRef} className="print-content">
            <TablePlan>
                <thead>
                    <tr>
                        <td colSpan={8} style={{ border: 'none', padding: 0 }}>
                            <Box className="d-flex flex-column align-items-center mb-3 text-center">
                                <Box sx={{ width: 80, height: 80, mb: 1 }}>
                                    <img src={branchLogo} onError={onBranchLogoError} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#333' }}>แผนการรักษา ตรวจจัดฟันและวางแผนการรักษา</Typography>
                                <Typography sx={{ fontSize: 16, color: '#666', mt: 0.5 }}>{clinicName}</Typography>
                            </Box>

                            <Box className="d-flex justify-content-between mb-3">
                                <Typography sx={{ fontSize: 14, color: '#333' }}><strong>ผู้ป่วย</strong> {props.patient?.firstname} {props.patient?.lastname} {props.patient?.cnNumber || props.patient?.hn}</Typography>
                                <Typography sx={{ fontSize: 14, color: '#333' }}><strong>วันที่</strong> {props.plan?.createdAt ? dateTimeToView(props.plan.createdAt) : '-'}</Typography>
                            </Box>
                        </td>
                    </tr>
                    <tr className="header-row">
                        <th>ลำดับ</th>
                        <th>ฟัน</th>
                        <th style={{ textAlign: 'left' }}>ประเภท</th>
                        <th style={{ textAlign: 'left' }}>รายการ/บริการ</th>
                        <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>{t(showTdhNotice ? 'TXP.TABLE.DATE_APPOINTMENT' : 'TXP.TABLE.DATE')}</th>
                        <th style={{ whiteSpace: 'nowrap' }}>ระยะเวลา</th>
                        <th>ราคาโดยประมาณ</th>
                        <th style={{ width: 140, textAlign: 'left' }}>ทันตแพทย์</th>
                    </tr>
                </thead>
                {groupedItems.length > 1 && (
                    <tbody>
                        {groupedItems.slice(0, -1).map((item: any, idx: number) => (
                            <tr key={idx} className="data-row">
                                <td>{idx + 1}</td>
                                <td>{item.teeth || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.operativeTypeName || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.operativeName || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.dateUse ? dateToView(item.dateUse) : '-'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{item.duration || '-'} {item.durationUnit ? t(`TXP.DATE.${item.durationUnit}`) : 'นาที'}</td>
                                <td style={{ textAlign: 'right' }}>{renderPlanPrice(item)}</td>
                                <td style={{ textAlign: 'left' }}>{doctorName}</td>
                            </tr>
                        ))}
                    </tbody>
                )}
                <tbody style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    {groupedItems.length > 0 && (() => {
                        const lastIdx = groupedItems.length - 1;
                        const item = groupedItems[lastIdx];
                        return (
                            <tr key={lastIdx} className="data-row">
                                <td>{lastIdx + 1}</td>
                                <td>{item.teeth || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.operativeTypeName || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.operativeName || '-'}</td>
                                <td style={{ textAlign: 'left' }}>{item.dateUse ? dateToView(item.dateUse) : '-'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{item.duration || '-'} {item.durationUnit ? t(`TXP.DATE.${item.durationUnit}`) : 'นาที'}</td>
                                <td style={{ textAlign: 'right' }}>{renderPlanPrice(item)}</td>
                                <td style={{ textAlign: 'left' }}>{doctorName}</td>
                            </tr>
                        );
                    })()}
                    <tr>
                        <td colSpan={8} style={{ border: 'none', padding: 0 }}>
                            <Box className="d-flex justify-content-end mb-4 pt-4">
                                <Box sx={{ border: `1px solid #C4C4C4`, px: 3, py: 1, borderRadius: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>ราคารวม {renderPlanTotal()}</Typography>
                                </Box>
                            </Box>

                            <Box className="mb-4">
                                {showTdhNotice ? <>
                                    <Typography sx={{ fontSize: 14, color: '#333' }}>
                                        <strong>หมายเหตุ</strong>{props.plan?.remark ? ` - ${props.plan.remark}` : ''}
                                    </Typography>
                                    <Typography sx={{ mt: 1, fontSize: 11, lineHeight: 1.45, color: '#555' }}>
                                        * {t('TXP.PRINT.TABLE.NOTICE_EN')}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, lineHeight: 1.45, color: '#555' }}>
                                        * {t('TXP.PRINT.TABLE.NOTICE_TH')}
                                    </Typography>
                                </> : (
                                    <Typography sx={{ fontSize: 14, color: '#333' }}><strong>หมายเหตุ</strong> - {props.plan?.remark || ''}</Typography>
                                )}
                            </Box>

                            <Box className="d-flex justify-content-between mt-4 pt-4 pb-2 px-3">
                                {/* Doctor Signature Block */}
                                <Box sx={{ width: 340, textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                        <StyledSignatureButton disabled={isActiveDoctorSaved || props.isReprint} onClick={() => { setModalType('doctor'); setOpenSignPad(true); }}>
                                            {signatures[activeDoctorId]?.doctor ? (
                                                <img src={`data:image/png;base64,${signatures[activeDoctorId]?.doctor}`} style={{ width: '160px', height: '90px', objectFit: 'contain' }} alt="doctor sig" />
                                            ) : (
                                                <>
                                                    <BtnEditUnderline color={'#808080'} width='28' height='28' />
                                                    <Typography sx={{ fontWeight: '500', fontSize: '14px' }}>{tdhLabel('CLICK_SIGNATURE')}</Typography>
                                                </>
                                            )}
                                        </StyledSignatureButton>
                                    </Box>
                                    <Box sx={{ borderBottom: "1px dashed black", width: "200px", margin: "0 auto", mb: 1 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{`( ${doctorName} )`}</Typography>
                                    <Typography sx={{ fontSize: 14, color: '#666' }}>ทันตแพทย์ผู้ให้การรักษา</Typography>
                                    <Typography sx={{ fontSize: 12, color: '#999' }}>Doctor&apos;s Signature</Typography>
                                </Box>

                                {/* Patient Signature Block */}
                                <Box sx={{ width: 340, textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                        <StyledSignatureButton disabled={isActiveDoctorSaved || props.isReprint} onClick={() => { setModalType('patient'); setOpenSignPad(true); }}>
                                            {signatures[activeDoctorId]?.patient ? (
                                                <img src={`data:image/png;base64,${signatures[activeDoctorId]?.patient}`} style={{ width: '160px', height: '90px', objectFit: 'contain' }} alt="patient sig" />
                                            ) : (
                                                <>
                                                    <BtnEditUnderline color={'#808080'} width='28' height='28' />
                                                    <Typography sx={{ fontWeight: '500', fontSize: '14px' }}>{tdhLabel('CLICK_SIGNATURE')}</Typography>
                                                </>
                                            )}
                                        </StyledSignatureButton>
                                    </Box>
                                    <Box sx={{ borderBottom: "1px dashed black", width: "200px", margin: "0 auto", mb: 1 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{`( ${props.patient?.firstname} ${props.patient?.lastname} )`}</Typography>
                                    <Typography sx={{ fontSize: 14, color: '#666' }}>ผู้รับบริการการรักษา</Typography>
                                    <Typography sx={{ fontSize: 12, color: '#999' }}>Patient&apos;s Signature</Typography>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                </tbody>
            </TablePlan>
        </PageContent>
    )

    const SignatureModalContent = (
        <Box className={`signatureWrap-box w-100`} key={modalType}>
            <SignBoxFee className='mx-auto'>
                <Box className={`clear-box`}>
                    <ButtonCustom variant={'outlined'} textButton={t('MODAL_TREATMENT_CONSENT.BUTTON.CLEAR_CANVAS')} onClick={() => handleClearSignature(modalType)} />
                </Box>
                <Box className="canvas-box">
                    <CanvasDraw
                        immediateLoading
                        key={`${modalType}-${canvasKey}`}
                        hideGrid
                        hideInterface
                        brushRadius={2}
                        brushColor={colors.black}
                        ref={modalType === 'doctor' ? canvasDoctorRef : canvasPatientRef}
                        canvasWidth={500}
                        canvasHeight={400}
                    />
                </Box>
            </SignBoxFee>
            <Box className='text-center mt-3'>
                <Typography sx={{ fontWeight: 500 }}>
                    {modalType === 'doctor' ? `( ${doctorName} )` : `( ${props.patient?.firstname} ${props.patient?.lastname} )`}
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                    {modalType === 'doctor' ? tdhLabel('DOCTOR_ROLE') : tdhLabel('PATIENT_ROLE')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#999' }}>
                    {modalType === 'doctor' ? tdhLabel('DOCTOR_SIGNATURE') : tdhLabel('PATIENT_SIGNATURE')}
                </Typography>
            </Box>
        </Box>
    )

    return (
        <>
            <StyledModal
                show={props.isShow}
                onHide={() => undefined}
                backdrop="static"
                size="xl"
                centered
                dialogClassName={showTdhNotice ? 'tdh-treatment-plan-dialog' : undefined}
            >
                <Modal.Header>
                    <Modal.Title>{documentTitle}</Modal.Title>
                    {showTdhNotice && (
                        <>
                            <Button
                                className='tdh-preview-language-trigger'
                                variant='outlined'
                                endIcon={<KeyboardArrowDownIcon fontSize='small' />}
                                aria-label={isEnglish ? 'Document language: English' : 'ภาษาเอกสาร: ไทย'}
                                aria-haspopup='menu'
                                aria-expanded={Boolean(languageMenuAnchor)}
                                onClick={(event) => setLanguageMenuAnchor(event.currentTarget)}
                                sx={getTreatmentPlanLanguageButtonStyle()}
                            >
                                {isEnglish ? 'English' : 'ไทย'}
                            </Button>
                            <Menu
                                anchorEl={languageMenuAnchor}
                                open={Boolean(languageMenuAnchor)}
                                onClose={() => setLanguageMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                MenuListProps={{ 'aria-label': 'Document language' }}
                            >
                                {(['th', 'en'] as TreatmentPlanPreviewLanguage[]).map((language) => (
                                    <MenuItem
                                        key={language}
                                        selected={previewLanguage === language}
                                        onClick={() => handlePreviewLanguageChange(language)}
                                        sx={{ minWidth: 120, fontSize: 14, gap: 1 }}
                                    >
                                        {language === 'th' ? 'ไทย' : 'English'}
                                        {previewLanguage === language && <CheckIcon sx={{ ml: 'auto', fontSize: 16, color: colors.themeSecondColor }} />}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Modal.Header>
                <Modal.Body className="custom-scroll p-0" style={showTdhNotice ? { maxHeight: 'calc(100vh - 190px)', overflow: 'auto' } : undefined}>
                    {uniqueDoctorIds.length > 1 && (
                        <Box className={showTdhNotice ? 'tdh-doctor-tabs' : undefined} sx={{ px: showTdhNotice ? 0 : 3 }}>
                            <UseStyled.DoctorTabs
                                value={doctorTab}
                                onChange={handleChangeTab}
                                variant="scrollable"
                                scrollButtons
                                allowScrollButtonsMobile
                            >
                                {uniqueDoctorIds.map((docId, index) => {
                                    const docData = props.doctors?.find(d => d.userId === docId)
                                    const name = docData
                                        ? (isEnglish
                                            ? docData.fullnameEn || [docData.firstnameEn, docData.lastnameEn].filter(Boolean).join(' ') || docData.fullname
                                            : docData.fullname || [docData.firstname, docData.lastname].filter(Boolean).join(' '))
                                        : (docId === 0 ? tdhLabel('UNASSIGNED_DOCTOR') : props.plan?.createdBy || '-')
                                    return <Tab key={index} label={<span className='text-ellipsis w-100'>{name}</span>} />
                                })}
                            </UseStyled.DoctorTabs>
                        </Box>
                    )}
                    <ToastContainer enableMultiContainer containerId={'popup'} className={'Toast-popup'} />
                    <Box sx={{ pb: 2, mx: 'auto', maxWidth: showTdhNotice ? 'none' : '1040px' }}>
                        {showTdhNotice ? TdhPageUI : LegacyPageUI}
                    </Box>
                </Modal.Body>
                <UseStyled.ModalFooter className='d-flex align-items-center justify-content-between px-3 pb-3'>
                    <Box className='d-flex align-items-center'>
                        <FunctionPrint
                            content={() => printRef.current}
                            docTitle={printTitle}
                            removeIfram
                            afterPrint={handleAfterPrint}
                            trigger={() => (
                                <ButtonCustom
                                    className={'btn-print'}
                                    variant={'outlined'}
                                    disabled={!isActiveDoctorSaved}
                                    textButton={t('BUTTON.PRINT') || 'พิมพ์'}
                                    onClick={() => null}
                                />
                            )}
                            style={showTdhNotice
                                ? getTdhTreatmentPlanPrintStyle()
                                : '@page { size: auto; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } }'}
                        />
                    </Box>
                    <Box className='d-flex align-items-center'>
                        <Box className='pr-2'>
                            <ButtonCustom
                                className={'btn-cancel'}
                                variant={'outlined'}
                                textButton={t('BUTTON.CLOSE') || 'ปิด'}
                                onClick={handleClose}
                            />
                        </Box>
                        {showSave && (
                            <Box className='pl-2'>
                                <ButtonCustom
                                    className={'btn-save'}
                                    disabled={!hasBothSignatures}
                                    textButton={t('BUTTON.SAVE') || 'บันทึก'}
                                    onClick={onSave}
                                />
                            </Box>
                        )}
                        {showCancelDocument && (
                            <Box className='pl-2'>
                                <ButtonCustom
                                    className={'btn-discard'}
                                    textButton={t('MODAL_TREATMENT_CONSENT.BUTTON.CANCEL_DOCUMENT') || 'ยกเลิกเอกสาร'}
                                    onClick={onCancel}
                                />
                            </Box>
                        )}
                    </Box>
                </UseStyled.ModalFooter>
            </StyledModal>
            {/* Signature Pad Modal */}
            <PageModal
                isOpen={openSignPad}
                size={'S'}
                title={t('PDF_INVOICE.PDF.SIGNATURE') || 'ลายเซ็น'}
                handleClose={() => setOpenSignPad(false)}
                onSubmit={{ onClick: () => handleSaveSignature(modalType) }}
                onCancel={{ onClick: () => setOpenSignPad(false) }}
                overflow
            >
                {SignatureModalContent}
            </PageModal>
        </>
    )
}
